import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  DeleteOutline,
  DesktopWindows,
  Download,
  EditOutlined,
  ImageOutlined,
  OpenInNew,
  PhoneIphone,
  Preview,
  Refresh,
  ViewAgenda,
  ViewColumn,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Loader from 'src/components/Loader';
import colors from 'src/theme/colors';
import { useProductPassport, useProductPassportImpacts } from 'src/Lca/hooks';
import ProductPassportRenderer from '../Renderer';
import PassportQrCode from '../PassportQrCode';
import PassportQrEditor from '../PassportQrEditor';
import { getPassportPath, getPassportUrl } from '../utils';
import {
  canAddPassportBlock,
  createDefaultPassportBranding,
  createDefaultPassportBlocks,
  createPassportBlock,
  getPassportBlockDataStatus,
  getPassportMissingFields,
  getResolvedPassportBlocks,
  getResolvedPassportBranding,
  PASSPORT_BLOCK_DEFINITIONS,
  PassportBrandingSettings,
  PassportLayoutBlock,
  PassportBlockDataStatus,
  PassportBlockType,
  resetPassportBlocks,
  resetPassportBranding,
  savePassportBlocks,
  savePassportBranding,
} from '../config';

interface ProductPassportBuilderProps {
  passportToken: string;
  productId?: number;
}

type PreviewDevice = 'desktop' | 'mobile';
type BuilderWorkspace = 'layout' | 'live';

const STATUS_CONFIG: Record<
  PassportBlockDataStatus,
  { color: 'success' | 'warning' | 'error'; tone: string }
> = {
  ready: {
    color: 'success',
    tone: alpha(colors.completed, 0.12),
  },
  partial: {
    color: 'warning',
    tone: 'rgba(237, 108, 2, 0.12)',
  },
  missing: {
    color: 'error',
    tone: 'rgba(211, 47, 47, 0.10)',
  },
};

const CORE_PASSPORT_AREAS = 8;
const BRAND_COLOR_OPTIONS = [
  colors.lcaMainDark,
  '#165D5C',
  '#1F4E79',
  '#7A2E3A',
  '#2E5E4E',
  '#A35A00',
];

const OPTIONAL_DEFINITIONS = Object.values(PASSPORT_BLOCK_DEFINITIONS).filter(
  (definition) => !definition.required
);

const getCustomQrExtension = (imageUrl: string, contentType: string) => {
  const fromUrl = imageUrl.split('?')[0].split('#')[0].split('.').pop();
  if (fromUrl && /^[a-z0-9]{2,5}$/i.test(fromUrl)) return fromUrl.toLowerCase();

  const fromContentType = contentType.split('/').pop();
  if (fromContentType && /^[a-z0-9]{2,5}$/i.test(fromContentType)) {
    return fromContentType.toLowerCase();
  }

  return 'png';
};

const getDataTabForBlock = (type: PassportBlockType) => {
  switch (type) {
    case 'carbon-footprint':
    case 'life-cycle-impact-details':
      return 'cycle';
    case 'durability-repairability':
    case 'recycling-end-of-life':
    case 'product-durability-reliability':
      return 'circularity';
    default:
      return 'prod-information';
  }
};

export default function ProductPassportBuilder({
  passportToken,
  productId,
}: ProductPassportBuilderProps) {
  const theme = useTheme();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation('lca');
  const { t: tPassport } = useTranslation('lca', {
    keyPrefix: 'productPassport',
  });
  const { product: passportProduct, isLoading } =
    useProductPassport(passportToken);
  const { impacts, isLoading: areImpactsLoading } = useProductPassportImpacts(
    passportToken,
    !!passportProduct
  );
  const product = useMemo(
    () =>
      passportProduct
        ? { ...passportProduct, impacts: impacts || passportProduct.impacts }
        : undefined,
    [impacts, passportProduct]
  );
  const [blocks, setBlocks] = useState<PassportLayoutBlock[]>(() =>
    getResolvedPassportBlocks(passportToken)
  );
  const [branding, setBranding] = useState<PassportBrandingSettings>(() =>
    getResolvedPassportBranding(passportToken)
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string>(
    getResolvedPassportBlocks(passportToken)[0]?.id || ''
  );
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [activeWorkspace, setActiveWorkspace] =
    useState<BuilderWorkspace>('layout');
  const [shouldFocusCustomContent, setShouldFocusCustomContent] =
    useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const qrContainerRef = useRef<HTMLDivElement | null>(null);
  const inspectorRef = useRef<HTMLDivElement | null>(null);
  const customContentInputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);

  useEffect(() => {
    const resolvedBlocks = getResolvedPassportBlocks(passportToken);
    const resolvedBranding = getResolvedPassportBranding(passportToken);
    setBlocks(resolvedBlocks);
    setBranding(resolvedBranding);
    setSelectedBlockId(resolvedBlocks[0]?.id || '');
  }, [passportToken]);

  useEffect(() => {
    if (!blocks.length) return;
    savePassportBlocks(passportToken, blocks);
  }, [blocks, passportToken]);

  useEffect(() => {
    savePassportBranding(passportToken, branding);
  }, [branding, passportToken]);

  useEffect(() => {
    if (!blocks.some((block) => block.id === selectedBlockId)) {
      setSelectedBlockId(blocks[0]?.id || '');
    }
  }, [blocks, selectedBlockId]);

  useEffect(() => {
    if (!isCompactLayout) {
      setActiveWorkspace('layout');
    }
  }, [isCompactLayout]);

  useEffect(() => {
    if (!shouldFocusCustomContent) return;

    const selectedBlock = blocks.find((block) => block.id === selectedBlockId);
    if (selectedBlock?.type !== 'custom-text') return;

    inspectorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    window.setTimeout(() => {
      customContentInputRef.current?.focus();
      setShouldFocusCustomContent(false);
    }, 0);
  }, [blocks, selectedBlockId, shouldFocusCustomContent]);

  if (isLoading || !product) {
    return <Loader />;
  }

  const passportPath = getPassportPath(passportToken);
  const passportUrl = getPassportUrl(passportToken);
  const customQrUrl = product.passport_qr_image_url;
  const missingFields = getPassportMissingFields(product, {
    impactsLoading: areImpactsLoading,
  });
  const completeness = Math.round(
    ((CORE_PASSPORT_AREAS - missingFields.length) / CORE_PASSPORT_AREAS) * 100
  );
  const selectedBlock = blocks.find((block) => block.id === selectedBlockId);
  const selectedBlockDefinition = selectedBlock
    ? PASSPORT_BLOCK_DEFINITIONS[selectedBlock.type]
    : null;
  const selectedBlockStatus = selectedBlock
    ? getPassportBlockDataStatus(product, selectedBlock.type, {
        impactsLoading: areImpactsLoading,
      })
    : null;

  const updateBlock = (
    blockId: string,
    updater: (_block: PassportLayoutBlock) => PassportLayoutBlock
  ) => {
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) =>
        block.id === blockId ? updater(block) : block
      )
    );
  };

  const updateBranding = (
    updater: (_branding: PassportBrandingSettings) => PassportBrandingSettings
  ) => {
    setBranding((currentBranding) => updater(currentBranding));
  };

  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    setBlocks((currentBlocks) => {
      const index = currentBlocks.findIndex((block) => block.id === blockId);
      if (index === -1) return currentBlocks;

      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= currentBlocks.length) {
        return currentBlocks;
      }

      const nextBlocks = [...currentBlocks];
      const [movedBlock] = nextBlocks.splice(index, 1);
      nextBlocks.splice(nextIndex, 0, movedBlock);
      return nextBlocks;
    });
  };

  const addBlock = (type: PassportBlockType) => {
    if (!canAddPassportBlock(blocks, type)) return;

    const newBlock = createPassportBlock(type);
    setBlocks((currentBlocks) => {
      const selectedIndex = currentBlocks.findIndex(
        (block) => block.id === selectedBlockId
      );

      if (selectedIndex === -1) {
        return [...currentBlocks, newBlock];
      }

      const nextBlocks = [...currentBlocks];
      nextBlocks.splice(selectedIndex + 1, 0, newBlock);
      return nextBlocks;
    });
    setSelectedBlockId(newBlock.id);
  };

  const removeBlock = (blockId: string) => {
    const block = blocks.find((item) => item.id === blockId);
    if (!block || block.required) return;

    setBlocks((currentBlocks) =>
      currentBlocks.filter((item) => item.id !== blockId)
    );
  };

  const editCustomBlock = (blockId: string) => {
    setActiveWorkspace('layout');
    setSelectedBlockId(blockId);
    setShouldFocusCustomContent(true);
  };

  const handleResetLayout = () => {
    resetPassportBlocks(passportToken);
    const defaults = createDefaultPassportBlocks();
    setBlocks(defaults);
    setSelectedBlockId(defaults[0]?.id || '');
  };

  const handleResetBranding = () => {
    resetPassportBranding(passportToken);
    setBranding(createDefaultPassportBranding());
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateBranding((currentBranding) => ({
        ...currentBranding,
        logoUrl: typeof reader.result === 'string' ? reader.result : null,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(passportUrl);
      toast.success(t('product.sharePage.toasts.copied') as string);
    } catch {
      toast.error(t('product.sharePage.toasts.copyFailed') as string);
    }
  };

  const triggerDownload = (href: string, extension: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = `product-passport-${passportToken}.${extension}`;
    link.click();
  };

  const downloadCustomQr = async (imageUrl: string) => {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('Custom QR code could not be fetched');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, getCustomQrExtension(imageUrl, blob.type));
    // give the browser time to pick up the download before the url is released
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const handleDownloadQr = async () => {
    try {
      // An uploaded QR code replaces the generated one, so there is no canvas to read.
      if (customQrUrl) {
        try {
          await downloadCustomQr(customQrUrl);
        } catch {
          // Uploaded images are served from the media domain, which may not allow
          // cross-origin reads - let the user save the file from a new tab instead.
          const opened = window.open(
            customQrUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (!opened) {
            throw new Error('Custom QR code could not be opened');
          }
          toast.success(
            t('product.sharePage.toasts.qrOpenedInNewTab') as string
          );
          return;
        }
      } else {
        const canvas = qrContainerRef.current?.querySelector('canvas');
        if (!canvas) {
          throw new Error('QR canvas not found');
        }

        triggerDownload(canvas.toDataURL('image/png'), 'png');
      }
      toast.success(t('product.sharePage.toasts.downloaded') as string);
    } catch {
      toast.error(t('product.sharePage.toasts.downloadFailed') as string);
    }
  };

  const workspaceToggle = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={activeWorkspace}
      onChange={(_event, value: BuilderWorkspace | null) => {
        if (value) {
          setActiveWorkspace(value);
        }
      }}
      sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
    >
      <ToggleButton value="layout" sx={{ px: 2 }}>
        <ViewColumn fontSize="small" sx={{ mr: 0.75 }} />
        {t('product.sharePage.builder.workspaces.layout')}
      </ToggleButton>
      <ToggleButton value="live" sx={{ px: 2 }}>
        <Preview fontSize="small" sx={{ mr: 0.75 }} />
        {t('product.sharePage.builder.workspaces.live')}
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const layoutContent = (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 1,
            }}
          >
            <Typography variant="h6">
              {t('product.sharePage.builder.canvasTitle')}
            </Typography>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={handleResetLayout}
            >
              {t('product.sharePage.builder.resetLayout')}
            </Button>
          </Box>
          <Typography color="text.secondary" mb={2}>
            {t('product.sharePage.builder.canvasDescription')}
          </Typography>

          <Stack spacing={1.5}>
            {blocks.map((block, index) => {
              const definition = PASSPORT_BLOCK_DEFINITIONS[block.type];
              const isSelected = block.id === selectedBlockId;
              const status = getPassportBlockDataStatus(product, block.type, {
                impactsLoading: areImpactsLoading,
              });

              return (
                <Box
                  key={block.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedBlockId(block.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      setSelectedBlockId(block.id);
                    }
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    backgroundColor: isSelected
                      ? 'rgba(126, 67, 8, 0.06)'
                      : 'background.paper',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>
                        {block.titleOverride || tPassport(definition.titleKey)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tPassport(definition.descriptionKey)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          moveBlock(block.id, 'up');
                        }}
                        disabled={index === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          moveBlock(block.id, 'down');
                        }}
                        disabled={index === blocks.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      {block.type === 'custom-text' && (
                        <Tooltip
                          title={
                            t('product.sharePage.builder.editSection') as string
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={
                              t(
                                'product.sharePage.builder.editSection'
                              ) as string
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              editCustomBlock(block.id);
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          removeBlock(block.id);
                        }}
                        disabled={block.required}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 1.5,
                    }}
                  >
                    <Chip
                      size="small"
                      label={
                        block.required
                          ? t('product.sharePage.builder.required')
                          : t('product.sharePage.builder.optional')
                      }
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        block.enabled
                          ? t('product.sharePage.builder.visible')
                          : t('product.sharePage.builder.hidden')
                      }
                    />
                    {definition.supportsWidth && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={
                          block.width === 'half' ? (
                            <ViewColumn />
                          ) : (
                            <ViewAgenda />
                          )
                        }
                        label={t(
                          `product.sharePage.builder.widths.${
                            block.width || 'full'
                          }`
                        )}
                      />
                    )}
                    <Chip
                      size="small"
                      color={STATUS_CONFIG[status.status].color}
                      label={t(
                        `product.sharePage.builder.status.${status.status}`
                      )}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={1}>
            {t('product.sharePage.builder.libraryTitle')}
          </Typography>
          <Typography color="text.secondary" mb={2}>
            {t('product.sharePage.builder.libraryDescription')}
          </Typography>
          <Stack spacing={1}>
            {OPTIONAL_DEFINITIONS.map((definition) => {
              const canAdd = canAddPassportBlock(blocks, definition.type);

              return (
                <Button
                  key={definition.type}
                  fullWidth
                  variant="outlined"
                  startIcon={<Add />}
                  disabled={!canAdd}
                  onClick={() => addBlock(definition.type)}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {tPassport(definition.titleKey)}
                </Button>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={1}>
            {t('product.sharePage.builder.completenessTitle')}
          </Typography>
          {missingFields.length === 0 ? (
            <Alert severity="success">
              {t('product.sharePage.builder.completenessReady')}
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('product.sharePage.builder.completenessMissing', {
                  count: missingFields.length,
                })}
              </Alert>
              <Stack spacing={1}>
                {missingFields.map((field) => (
                  <Typography key={field.id} variant="body2">
                    • {tPassport(field.labelKey)}
                  </Typography>
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 1,
            }}
          >
            <Typography variant="h6">
              {t('product.sharePage.builder.brandingTitle')}
            </Typography>
            <Button size="small" onClick={handleResetBranding}>
              {t('product.sharePage.builder.resetBranding')}
            </Button>
          </Box>
          <Typography color="text.secondary" mb={2}>
            {t('product.sharePage.builder.brandingDescription')}
          </Typography>

          <Stack spacing={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '16px',
                border: `1px solid ${alpha(branding.accentColor, 0.18)}`,
                backgroundColor: alpha(branding.accentColor, 0.05),
              }}
            >
              <Typography fontWeight={700} mb={1}>
                {t('product.sharePage.builder.brandPreview')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '999px',
                    backgroundColor: branding.accentColor,
                    border: `1px solid ${alpha(branding.accentColor, 0.35)}`,
                  }}
                />
                <Typography variant="body2">{branding.accentColor}</Typography>
              </Stack>
              {branding.logoUrl && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '12px',
                    backgroundColor: colors.pureWhite,
                    border: `1px solid ${alpha(branding.accentColor, 0.16)}`,
                    display: 'inline-flex',
                  }}
                >
                  <img
                    src={branding.logoUrl}
                    alt={t('product.sharePage.builder.logoAlt')}
                    style={{
                      display: 'block',
                      maxWidth: '160px',
                      maxHeight: '48px',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              type="color"
              label={t('product.sharePage.builder.brandColorLabel')}
              value={branding.accentColor}
              onChange={(event) =>
                updateBranding((currentBranding) => ({
                  ...currentBranding,
                  accentColor: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('product.sharePage.builder.brandColorPresets')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {BRAND_COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      updateBranding((currentBranding) => ({
                        ...currentBranding,
                        accentColor: color,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        updateBranding((currentBranding) => ({
                          ...currentBranding,
                          accentColor: color,
                        }));
                      }
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '999px',
                      backgroundColor: color,
                      cursor: 'pointer',
                      border:
                        branding.accentColor === color
                          ? `2px solid ${colors.dark}`
                          : `1px solid ${colors.backgroundGreyDark}`,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              hidden
              onChange={handleLogoUpload}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="secondary"
                startIcon={<ImageOutlined />}
                onClick={() => logoInputRef.current?.click()}
              >
                {branding.logoUrl
                  ? t('product.sharePage.builder.changeLogo')
                  : t('product.sharePage.builder.uploadLogo')}
              </Button>
              {branding.logoUrl && (
                <Button
                  variant="secondary"
                  color="error"
                  onClick={() =>
                    updateBranding((currentBranding) => ({
                      ...currentBranding,
                      logoUrl: null,
                    }))
                  }
                >
                  {t('product.sharePage.builder.removeLogo')}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card ref={inspectorRef}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            {t('product.sharePage.builder.inspectorTitle')}
          </Typography>
          {!selectedBlock ||
          !selectedBlockDefinition ||
          !selectedBlockStatus ? (
            <Typography color="text.secondary">
              {t('product.sharePage.builder.noSelection')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '16px',
                  backgroundColor:
                    STATUS_CONFIG[selectedBlockStatus.status].tone,
                }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                  <Chip
                    size="small"
                    color={STATUS_CONFIG[selectedBlockStatus.status].color}
                    label={t(
                      `product.sharePage.builder.status.${selectedBlockStatus.status}`
                    )}
                  />
                  <Chip
                    size="small"
                    label={t('product.sharePage.builder.statusSummary', {
                      filled: selectedBlockStatus.filled,
                      total: selectedBlockStatus.total,
                    })}
                  />
                </Stack>
                <Typography variant="body2">
                  {tPassport(selectedBlockDefinition.descriptionKey)}
                </Typography>
              </Box>

              <TextField
                label={t('product.sharePage.builder.blockTitleLabel')}
                value={selectedBlock.titleOverride || ''}
                onChange={(event) =>
                  updateBlock(selectedBlock.id, (block) => ({
                    ...block,
                    titleOverride: event.target.value,
                  }))
                }
                helperText={t('product.sharePage.builder.blockTitleHelper')}
                fullWidth
              />

              {selectedBlockDefinition.supportsWidth ? (
                <TextField
                  select
                  fullWidth
                  label={t('product.sharePage.builder.blockWidthLabel')}
                  value={selectedBlock.width || 'full'}
                  onChange={(event) =>
                    updateBlock(selectedBlock.id, (block) => ({
                      ...block,
                      width: event.target.value as 'full' | 'half',
                    }))
                  }
                >
                  <MenuItem value="full">
                    {t('product.sharePage.builder.widths.full')}
                  </MenuItem>
                  <MenuItem value="half">
                    {t('product.sharePage.builder.widths.half')}
                  </MenuItem>
                </TextField>
              ) : (
                <Alert severity="info">
                  {t('product.sharePage.builder.widthLocked')}
                </Alert>
              )}

              {selectedBlockDefinition.supportsVariant && (
                <TextField
                  select
                  fullWidth
                  label={t('product.sharePage.builder.blockVariantLabel')}
                  value={selectedBlock.variant || 'table'}
                  onChange={(event) =>
                    updateBlock(selectedBlock.id, (block) => ({
                      ...block,
                      variant: event.target.value as 'table' | 'cards',
                    }))
                  }
                >
                  <MenuItem value="table">
                    {t('product.sharePage.builder.variants.table')}
                  </MenuItem>
                  <MenuItem value="cards">
                    {t('product.sharePage.builder.variants.cards')}
                  </MenuItem>
                </TextField>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px',
                  px: 1.5,
                  py: 1,
                }}
              >
                <Box>
                  <Typography fontWeight={700}>
                    {t('product.sharePage.builder.blockVisibilityLabel')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedBlock.required
                      ? t('product.sharePage.builder.cannotRemove')
                      : t('product.sharePage.builder.optional')}
                  </Typography>
                </Box>
                <Switch
                  checked={selectedBlock.enabled}
                  disabled={selectedBlock.required}
                  onChange={(event) =>
                    updateBlock(selectedBlock.id, (block) => ({
                      ...block,
                      enabled: event.target.checked,
                    }))
                  }
                />
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="secondary"
                  startIcon={<ArrowUpward />}
                  onClick={() => moveBlock(selectedBlock.id, 'up')}
                  disabled={blocks[0]?.id === selectedBlock.id}
                >
                  {t('product.sharePage.builder.moveUp')}
                </Button>
                <Button
                  fullWidth
                  variant="secondary"
                  startIcon={<ArrowDownward />}
                  onClick={() => moveBlock(selectedBlock.id, 'down')}
                  disabled={blocks[blocks.length - 1]?.id === selectedBlock.id}
                >
                  {t('product.sharePage.builder.moveDown')}
                </Button>
              </Stack>

              {(selectedBlock.type === 'custom-text' ||
                selectedBlock.type === 'about-dpp-lca') && (
                <TextField
                  label={t('product.sharePage.builder.blockContentLabel')}
                  inputRef={
                    selectedBlock.type === 'custom-text'
                      ? customContentInputRef
                      : undefined
                  }
                  value={
                    selectedBlock.type === 'about-dpp-lca'
                      ? (selectedBlock.content ??
                        t('product.sharePage.builder.defaultDppLcaText'))
                      : selectedBlock.content || ''
                  }
                  onChange={(event) =>
                    updateBlock(selectedBlock.id, (block) => ({
                      ...block,
                      content: event.target.value,
                    }))
                  }
                  multiline
                  minRows={8}
                  fullWidth
                />
              )}

              {productId && selectedBlockStatus.status !== 'ready' && (
                <Button
                  component="a"
                  href={`/lca/products/${productId}#${getDataTabForBlock(
                    selectedBlock.type
                  )}`}
                  variant="secondary"
                >
                  {t('product.sharePage.builder.openProductData')}
                </Button>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );

  const liveContent = (
    <Stack spacing={3}>
      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6">
              {t('product.sharePage.builder.previewTitle')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={previewDevice}
              onChange={(_event, value: PreviewDevice | null) => {
                if (value) {
                  setPreviewDevice(value);
                }
              }}
            >
              <ToggleButton value="desktop">
                <DesktopWindows fontSize="small" sx={{ mr: 0.75 }} />
                {t('product.sharePage.builder.devices.desktop')}
              </ToggleButton>
              <ToggleButton value="mobile">
                <PhoneIphone fontSize="small" sx={{ mr: 0.75 }} />
                {t('product.sharePage.builder.devices.mobile')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography color="text.secondary" mb={2}>
            {t('product.sharePage.builder.previewDescription')}
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('product.sharePage.builder.publicDraftNotice')}
          </Alert>
          <Divider sx={{ mb: 3 }} />
          <Box
            sx={{
              mx: 'auto',
              width: '100%',
              maxWidth: previewDevice === 'mobile' ? 430 : '100%',
              transition: 'max-width 0.2s ease',
            }}
          >
            <ProductPassportRenderer
              product={product}
              blocks={blocks}
              branding={branding}
              passportUrl={passportUrl}
              impactsLoading={areImpactsLoading}
              isPreview
              forceMobileLayout={previewDevice === 'mobile'}
            />
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            {t('product.sharePage.passportLink')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              value={passportUrl}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="secondary"
                onClick={handleCopyLink}
                startIcon={<ContentCopy />}
              >
                {t('product.sharePage.copyLink')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.open(passportPath, '_blank')}
                startIcon={<OpenInNew />}
              >
                {t('product.sharePage.openPassport')}
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" mb={2}>
            {t('product.sharePage.qrCode')}
          </Typography>
          <Stack spacing={2} alignItems="flex-start">
            <Box
              ref={qrContainerRef}
              sx={{
                width: 240,
                minHeight: 240,
                border: `1px solid ${colors.backgroundGrey}`,
                borderRadius: '16px',
                p: 1,
                backgroundColor: colors.pureWhite,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PassportQrCode
                passportUrl={passportUrl}
                size={224}
                imageUrl={customQrUrl}
                gtinCode={product.gtin_code}
              />
            </Box>

            <Button
              variant="secondary"
              onClick={handleDownloadQr}
              startIcon={<Download />}
            >
              {t('product.sharePage.downloadQr')}
            </Button>
          </Stack>

          {productId !== undefined && (
            <PassportQrEditor
              productId={productId}
              passportToken={passportToken}
              passportUrl={passportUrl}
            />
          )}
        </CardContent>
      </Card>
    </Stack>
  );

  const compactContent =
    activeWorkspace === 'layout' ? layoutContent : liveContent;

  const mainContent = isCompactLayout ? (
    compactContent
  ) : (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(320px, 1fr) minmax(0, 2fr)',
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Box
        sx={{
          borderRadius: '24px',
          p: 1,
          backgroundColor:
            activeWorkspace === 'layout'
              ? alpha(colors.lcaMainDark, 0.03)
              : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
      >
        {layoutContent}
      </Box>

      <Box
        sx={{
          position: 'sticky',
          top: 24,
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          borderRadius: '24px',
          p: 1,
          backgroundColor:
            activeWorkspace === 'live'
              ? alpha(colors.lcaMainDark, 0.03)
              : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
      >
        {liveContent}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" mb={1}>
          {t('product.sharePage.builder.title')}
        </Typography>
        <Typography color="text.secondary" mb={2}>
          {t('product.sharePage.builder.description')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
          <Chip
            color={missingFields.length === 0 ? 'success' : 'warning'}
            label={t('product.sharePage.builder.completenessPercent', {
              value: completeness,
            })}
          />
          <Chip
            variant="outlined"
            label={t('product.sharePage.builder.autosaved')}
          />
        </Stack>
        <Alert severity="info">
          {t('product.sharePage.builder.draftNotice')}
        </Alert>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            {t('product.sharePage.builder.stageLabel')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isCompactLayout
              ? t('product.sharePage.builder.mobileWorkspaceHint')
              : t('product.sharePage.builder.desktopWorkspaceHint')}
          </Typography>
        </Box>
        {workspaceToggle}
      </Box>

      {mainContent}
    </Box>
  );
}
